package com.evoapps.evoshift.ui.util

import androidx.compose.runtime.Composable
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AbsenceType
import evoshift.composeapp.generated.resources.absence_type_force_majeure
import evoshift.composeapp.generated.resources.absence_type_on_demand
import evoshift.composeapp.generated.resources.absence_type_sick_leave
import evoshift.composeapp.generated.resources.absence_type_vacation
import evoshift.composeapp.generated.resources.*
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.model.UserPermission
import evoshift.composeapp.generated.resources.permission_owner
import evoshift.composeapp.generated.resources.permission_manager_financial
import evoshift.composeapp.generated.resources.permission_manager
import evoshift.composeapp.generated.resources.permission_employee
import evoshift.composeapp.generated.resources.absence_status_approved
import evoshift.composeapp.generated.resources.absence_status_pending
import evoshift.composeapp.generated.resources.absence_status_rejected
import evoshift.composeapp.generated.resources.availability_available
import evoshift.composeapp.generated.resources.availability_unavailable
import evoshift.composeapp.generated.resources.swap_status_available
import evoshift.composeapp.generated.resources.swap_status_rejected
import evoshift.composeapp.generated.resources.swap_status_requested_by_owner
import evoshift.composeapp.generated.resources.swap_status_taken_pending
import com.evoapps.evoshift.domain.model.CostFrequency
import com.evoapps.evoshift.domain.model.RosterStatus
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.frequency_monthly
import evoshift.composeapp.generated.resources.frequency_one_off
import evoshift.composeapp.generated.resources.frequency_weekly
import evoshift.composeapp.generated.resources.frequency_yearly
import evoshift.composeapp.generated.resources.roster_status_archived
import evoshift.composeapp.generated.resources.roster_status_draft
import evoshift.composeapp.generated.resources.roster_status_locked
import evoshift.composeapp.generated.resources.roster_status_published
import evoshift.composeapp.generated.resources.roster_status_skeleton
import org.jetbrains.compose.resources.stringResource

/** User-facing names of domain enums (never print `.name`, it is an English code). */
@Composable
fun RosterStatus.label(): String = stringResource(
    when (this) {
        RosterStatus.SKELETON -> Res.string.roster_status_skeleton
        RosterStatus.DRAFT -> Res.string.roster_status_draft
        RosterStatus.PUBLISHED -> Res.string.roster_status_published
        RosterStatus.LOCKED -> Res.string.roster_status_locked
        RosterStatus.ARCHIVED -> Res.string.roster_status_archived
    }
)

@Composable
fun CostFrequency.label(): String = stringResource(
    when (this) {
        CostFrequency.MONTHLY -> Res.string.frequency_monthly
        CostFrequency.WEEKLY -> Res.string.frequency_weekly
        CostFrequency.YEARLY -> Res.string.frequency_yearly
        CostFrequency.ONE_OFF -> Res.string.frequency_one_off
    }
)

@Composable
fun SwapStatus.label(): String = stringResource(
    when (this) {
        SwapStatus.REQUESTED_BY_OWNER -> Res.string.swap_status_requested_by_owner
        SwapStatus.AVAILABLE -> Res.string.swap_status_available
        SwapStatus.TAKEN_PENDING -> Res.string.swap_status_taken_pending
        SwapStatus.REJECTED -> Res.string.swap_status_rejected
    }
)

@Composable
fun AbsenceStatus.label(): String = stringResource(
    when (this) {
        AbsenceStatus.PENDING -> Res.string.absence_status_pending
        AbsenceStatus.APPROVED -> Res.string.absence_status_approved
        AbsenceStatus.REJECTED -> Res.string.absence_status_rejected
    }
)

@Composable
fun AvailabilityStatus.label(): String = stringResource(
    when (this) {
        AvailabilityStatus.AVAILABLE -> Res.string.availability_available
        AvailabilityStatus.UNAVAILABLE -> Res.string.availability_unavailable
    }
)

@Composable
fun AbsenceType.label(): String = stringResource(labelRes())

/** SH-223: every request type's name (also for getString, the printed reports). */
fun AbsenceType.labelRes(): org.jetbrains.compose.resources.StringResource = when (this) {
    AbsenceType.SICK_LEAVE -> Res.string.absence_type_sick_leave
    AbsenceType.VACATION -> Res.string.absence_type_vacation
    AbsenceType.ON_DEMAND -> Res.string.absence_type_on_demand
    AbsenceType.FORCE_MAJEURE -> Res.string.absence_type_force_majeure
    AbsenceType.OCCASIONAL_LEAVE -> Res.string.absence_type_occasional_leave
    AbsenceType.BLOOD_DONATION -> Res.string.absence_type_blood_donation
    AbsenceType.TIME_OFF_IN_LIEU -> Res.string.absence_type_time_off_in_lieu
    AbsenceType.CHILDCARE -> Res.string.absence_type_childcare
    AbsenceType.CARER_LEAVE -> Res.string.absence_type_carer_leave
    AbsenceType.CARE_ALLOWANCE -> Res.string.absence_type_care_allowance
    AbsenceType.UNPAID_LEAVE -> Res.string.absence_type_unpaid_leave
    AbsenceType.MATERNITY -> Res.string.absence_type_maternity
    AbsenceType.PATERNITY -> Res.string.absence_type_paternity
    AbsenceType.PARENTAL -> Res.string.absence_type_parental
    AbsenceType.CHILDCARE_LEAVE -> Res.string.absence_type_childcare_leave
    AbsenceType.QUARANTINE -> Res.string.absence_type_quarantine
}

/** SH-223: the work requests' names. */
fun com.evoapps.evoshift.domain.model.WorkRequestKind.labelRes(): org.jetbrains.compose.resources.StringResource = when (this) {
    com.evoapps.evoshift.domain.model.WorkRequestKind.REMOTE_WORK -> Res.string.work_kind_remote
    com.evoapps.evoshift.domain.model.WorkRequestKind.WORK_EVENT -> Res.string.work_kind_event
    com.evoapps.evoshift.domain.model.WorkRequestKind.OVERTIME -> Res.string.work_kind_overtime
}

@Composable
fun com.evoapps.evoshift.domain.model.WorkRequestKind.label(): String = stringResource(labelRes())

@Composable
fun UserPermission.label(): String = stringResource(
    when (this) {
        UserPermission.OWNER -> Res.string.permission_owner
        UserPermission.MANAGER_FINANCIAL -> Res.string.permission_manager_financial
        UserPermission.MANAGER -> Res.string.permission_manager
        UserPermission.EMPLOYEE -> Res.string.permission_employee
    }
)

/** SH-270: a feature's name in the access editor. */
@Composable
fun com.evoapps.evoshift.domain.model.Feature.label(): String = stringResource(
    when (this) {
        com.evoapps.evoshift.domain.model.Feature.ROSTER -> Res.string.feature_roster
        com.evoapps.evoshift.domain.model.Feature.TEMPLATES -> Res.string.feature_templates
        com.evoapps.evoshift.domain.model.Feature.AVAILABILITY -> Res.string.feature_availability
        com.evoapps.evoshift.domain.model.Feature.REQUESTS -> Res.string.feature_requests
        com.evoapps.evoshift.domain.model.Feature.TEAM -> Res.string.feature_team
        com.evoapps.evoshift.domain.model.Feature.HIRING -> Res.string.feature_hiring
        com.evoapps.evoshift.domain.model.Feature.TRAINING -> Res.string.feature_training
        com.evoapps.evoshift.domain.model.Feature.CHECKLISTS -> Res.string.feature_checklists
        com.evoapps.evoshift.domain.model.Feature.ANNOUNCEMENTS -> Res.string.feature_announcements
        com.evoapps.evoshift.domain.model.Feature.CLOCK -> Res.string.feature_clock
        com.evoapps.evoshift.domain.model.Feature.TIPS -> Res.string.feature_tips
        com.evoapps.evoshift.domain.model.Feature.SETTINGS -> Res.string.feature_settings
        com.evoapps.evoshift.domain.model.Feature.REPORTS -> Res.string.feature_reports
        com.evoapps.evoshift.domain.model.Feature.PAY -> Res.string.feature_pay
        com.evoapps.evoshift.domain.model.Feature.FINANCES -> Res.string.feature_finances
        com.evoapps.evoshift.domain.model.Feature.TAKINGS -> Res.string.feature_takings
    }
)

/** SH-270: none / view / edit. */
@Composable
fun com.evoapps.evoshift.domain.model.AccessLevel.label(): String = stringResource(
    when (this) {
        com.evoapps.evoshift.domain.model.AccessLevel.NONE -> Res.string.access_level_none
        com.evoapps.evoshift.domain.model.AccessLevel.VIEW -> Res.string.access_level_view
        com.evoapps.evoshift.domain.model.AccessLevel.EDIT -> Res.string.access_level_edit
    }
)

/** SH-270: a role's name: a built-in role by its permission's name, a custom one by its own. */
@Composable
fun com.evoapps.evoshift.domain.model.RoleChoice.label(): String = builtIn?.permission?.label() ?: custom?.name.orEmpty()
